'use client';

import { useEffect } from 'react';
import { useLanguage } from '@/lib/translation';
import { getLocaleFromCookieClient, setLocaleCookieClient } from '@/lib/translation/cookie-utils';
import i18n from '@/lib/translation/i18n';

export function I18nProvider({
  children,
  initialLanguage,
}: {
  children: React.ReactNode;
  initialLanguage?: string;
}) {
  // Keep SSR language in sync to avoid hydration mismatches.
  if (typeof window === 'undefined' && initialLanguage && i18n.language !== initialLanguage) {
    i18n.changeLanguage(initialLanguage);
  }

  // One-time cleanup: remove any stale i18nextLng localStorage key left by the old
  // LanguageDetector config (which cached language there). The key is no longer read
  // or written, but existing browsers may still have it set to "he", which would
  // have caused ClerkProvider to mount with the wrong localization.
  useEffect(() => {
    try { localStorage.removeItem('i18nextLng'); } catch { /* ignore */ }
  }, []);

  const { currentLanguage } = useLanguage();
  
  useEffect(() => {
    // Sync cookie with current i18n language on mount
    // This ensures cookie is set even if user hasn't changed language yet
    const cookieLocale = getLocaleFromCookieClient();
    if (cookieLocale !== currentLanguage.code) {
      // Cookie doesn't match i18n language, sync it
      setLocaleCookieClient(currentLanguage.code);
    }
  }, [currentLanguage.code]);

  useEffect(() => {
    // Keep client document attributes in sync with SSR cookie/lang
    if (typeof document !== 'undefined') {
      document.documentElement.lang = currentLanguage.code;
      document.documentElement.dir = currentLanguage.direction;
    }
  }, [currentLanguage.code]);

  return <>{children}</>;
}
